// Spanish copy for /face-data-policy (hand-written from copy.en.ts; same shape, same facts). The detailed documents
// it links to (the Studio biometric data retention policy and the full Photohawk Family policy) are in English.
import type { FaceDataPolicyCopy } from "./copy.en";

const CONTACT =
  "CLOUDCONCUR LTD (empresa n.º 13399913), que opera como Photohawk, 23 Caldey Gardens, Ingleby Barwick, Stockton-on-Tees, TS17 5HW, Reino Unido. Correo: support@photohawk.com.";

const es: FaceDataPolicyCopy = {
  seo: {
    title: "Política de datos faciales | Photohawk",
    description:
      "Qué datos faciales crea Photohawk, dónde se tratan, para qué se usan y cuándo se eliminan: una política para Photohawk Studio (galerías de eventos y empresas) y otra para la app Photohawk (la app para las fotos de tu gente).",
  },
  title: "Política de datos faciales",
  version: "1.1",
  effectiveDate: "22 de octubre de 2026",
  meta: "Versión {version} · En vigor desde el {date}",
  intro: [
    "Photohawk tiene dos productos que encuentran a las personas en las fotos, y cada uno tiene su propia política de datos faciales a continuación. Photohawk Studio gestiona galerías de eventos y empresas, donde cada persona encuentra sus fotos con un selfie. La app Photohawk es para las fotos de tu gente, en tu propio teléfono.",
    "Los dos se mantienen separados. Los datos faciales de las galerías de Photohawk Studio y los de la app Photohawk nunca se comparten, se mezclan ni se comparan entre sí. Ninguno se vende ni se usa para publicidad, y los datos faciales nunca son una foto: eliminarlos nunca elimina una foto.",
  ],
  tabsLabel: "Elige un producto",
  studio: {
    id: "studio",
    tab: "Photohawk Studio",
    heading: "Photohawk Studio — galerías de eventos y empresas",
    summary:
      "La búsqueda por selfie es opcional y la activa el administrador de cada galería. Permite que una persona encuentre sus propias fotos en esa galería con un selfie.",
    sections: [
      {
        heading: "1. Qué datos faciales se crean",
        blocks: [
          {
            type: "ul",
            items: [
              "Plantillas faciales: representaciones numéricas de los rostros detectados en las fotos de una galería, guardadas en el índice de búsqueda facial de esa galería (Amazon Rekognition) o, en las galerías que usan uno de nuestros propios modelos faciales, como vectores faciales.",
              "El archivo de rostros: pequeños recortes de rostros guardados de forma privada para poder reconstruir la búsqueda facial de una galería sin volver a leer las fotos originales. Nunca se muestra a nadie ni se publica.",
              "Los selfies usados para buscar no son datos faciales que guardemos: un selfie se compara con las plantillas faciales de esa galería y después se descarta. No se guarda y no se crea ninguna plantilla facial a partir de él.",
            ],
          },
          {
            type: "p",
            text: "Encontrar personas en las fotos (cuerpos completos, sin identificar a nadie) y leer dorsales y texto no usa datos faciales. La búsqueda por dorsal no se ve afectada por esta política.",
          },
        ],
      },
      {
        heading: "2. Dónde se tratan",
        blocks: [
          {
            type: "ul",
            items: [
              "En la nube de Photohawk, en Amazon Web Services en la Unión Europea (Irlanda, eu-west-1), después de subir una foto.",
              "Cada galería tiene su propio índice de búsqueda facial (una colección de Amazon Rekognition). Un selfie solo se busca en la galería para la que se hizo.",
              "Para ahorrar tiempo, las apps de subida de Photohawk Studio pueden encontrar los rostros en el ordenador o el teléfono que sube las fotos y enviar pequeños recortes de rostros con la foto. Las plantillas faciales siempre se crean en nuestra nube.",
            ],
          },
        ],
      },
      {
        heading: "3. Para qué se usan y para qué nunca se usan",
        blocks: [
          {
            type: "p",
            text: "Los datos faciales se usan solo para que las personas encuentren sus propias fotos en esa galería con un selfie.",
          },
          {
            type: "p",
            text: "Nunca se venden, nunca se usan para publicidad, nunca se usan para identificar a nadie fuera de la galería y nunca se mezclan ni se comparan con los datos faciales de la app Photohawk.",
          },
        ],
      },
      {
        heading: "4. Cuánto tiempo se guardan y cuándo se eliminan",
        blocks: [
          {
            type: "p",
            text: "A partir del 1 de noviembre de 2026, los datos faciales se eliminan de forma permanente en el primero de estos momentos:",
          },
          {
            type: "ul",
            items: [
              "12 meses después de subir la foto (foto a foto, aunque la galería siga en línea);",
              "30 días después de cerrar o eliminar la galería, o de desactivar su búsqueda facial (se cancela si el administrador la reabre o vuelve a activar la búsqueda facial en esos 30 días);",
              "30 días después de que una persona nos pida eliminar sus datos faciales o retire su consentimiento;",
              "45 días después de que nuestra revisión anual determine que ya no son necesarios.",
            ],
          },
          {
            type: "p",
            text: "Hasta el 31 de octubre de 2026, los datos faciales se eliminan cuando una galería caduca (en unos 60 días) y cuando se elimina una galería. Las plantillas faciales no tienen copia de seguridad, así que una eliminación es definitiva; las copias de seguridad de nuestra base de datos solo contienen referencias a rostros (nunca plantillas) y se purgan en 35 días. Las fotos, las ventas, las descargas y la búsqueda por dorsal no se ven afectadas.",
          },
          {
            type: "link",
            text: "El calendario de conservación completo y cómo funciona la eliminación (en inglés):",
            label: "Biometric data retention policy",
            href: "/biometric-data-retention",
          },
        ],
      },
      {
        heading: "5. Base jurídica",
        blocks: [
          {
            type: "p",
            text: "Fotos. Las fotos de una galería se comparten para que los participantes puedan encontrar y comprar sus propias fotos. Se tratan porque es necesario para la finalidad de la galería, y se conservan solo durante el tiempo proporcionado a esa finalidad (véase §4).",
          },
          {
            type: "p",
            text: "Búsqueda facial. Los datos faciales son datos biométricos y reciben una protección adicional:",
          },
          {
            type: "ul",
            items: [
              "Cuándo se usan: solo para que las personas puedan encontrar sus propias fotos en esa galería. Nunca se usan para identificar a nadie con ningún otro fin.",
              "Cuánto tiempo se guardan: solo durante un tiempo limitado (§4). Se eliminan en cuanto dejan de ser necesarios.",
              "A la persona que busca se le pide su consentimiento explícito antes de usar su selfie. El selfie nunca se guarda.",
              "El administrador decide si la búsqueda facial es adecuada para su evento y si está permitida en el lugar donde se celebra. Cuando la legislación local exige el consentimiento de las personas que aparecen en las fotos, el administrador debe tenerlo, por ejemplo a través de la inscripción en el evento. Si no lo tiene, debe usar en su lugar la búsqueda por dorsal y por hora, que no usa datos faciales.",
              "Cualquier persona puede pedir en cualquier momento que se eliminen sus datos faciales, y se eliminan en un plazo de 30 días (§8).",
            ],
          },
          {
            type: "p",
            text: "Para las personas de una galería, el administrador de la galería es el responsable del tratamiento; Photohawk trata los datos faciales en su nombre, con nuestro acuerdo de tratamiento de datos.",
          },
        ],
      },
      {
        heading: "6. Illinois y otros estados de EE. UU.",
        blocks: [
          {
            type: "p",
            text: "Esta política y la política de conservación de datos biométricos son nuestro calendario de conservación público y por escrito. Los límites de eliminación del §4 están dentro de los plazos de la ley de Illinois sobre información biométrica (BIPA: cuando se cumple la finalidad o en un plazo de 3 años desde la última interacción de la persona), la CUBI de Texas, la Ley de Privacidad de Colorado y la RCW 19.375 de Washington. El límite de 12 meses es una decisión propia de Photohawk; ninguna ley lo establece.",
          },
        ],
      },
      {
        heading: "7. Quién los trata",
        blocks: [
          {
            type: "p",
            text: "Amazon Web Services (Amazon Rekognition y almacenamiento), región UE (Irlanda), como nuestro subencargado, solo siguiendo nuestras instrucciones. No revelamos datos faciales a nadie más salvo que la ley lo exija.",
          },
        ],
      },
      {
        heading: "8. Solicitudes y contacto",
        blocks: [
          {
            type: "ul",
            items: [
              "En la galería: abre la búsqueda por selfie y elige “Make a request”. La solicitud llega al administrador de la galería y a Photohawk.",
              "Por correo: support@photohawk.com, indicando a qué galería o evento se refiere. Atendemos la solicitud en un plazo de 30 días.",
              CONTACT,
            ],
          },
        ],
      },
    ],
    full: {
      text: "El calendario de conservación detallado de Studio (en inglés):",
      label: "Biometric data retention policy",
      href: "/biometric-data-retention",
    },
  },
  family: {
    id: "family",
    tab: "App Photohawk",
    heading: "La app Photohawk — para las fotos de tu gente",
    summary:
      "La agrupación de caras es opcional. La app (en las tiendas como “Photohawk”) pide permiso antes de crear datos faciales, y las caras se encuentran en tu teléfono.",
    sections: [
      {
        heading: "1. Qué datos faciales se crean",
        blocks: [
          {
            type: "ul",
            items: [
              "Con la agrupación de caras activada, en tu teléfono: plantillas faciales (una lista de números por cada cara encontrada), dónde están las caras en cada foto o vídeo y qué caras pertenecen a la misma persona. Los nombres y las correcciones que añades se quedan solo en tu teléfono.",
              "Si además activas la comparación de caras en la nube: pequeños recortes de caras (nunca fotos ni vídeos completos) y las plantillas faciales y los grupos que nuestro servicio en la nube crea a partir de ellos.",
            ],
          },
          {
            type: "p",
            text: "Encontrar personas (cuerpos completos, sin identificar a nadie) y cosas en tus fotos no usa datos faciales y funciona con la agrupación de caras desactivada.",
          },
        ],
      },
      {
        heading: "2. Dónde se tratan",
        blocks: [
          {
            type: "ul",
            items: [
              "En tu teléfono: las caras se encuentran en el teléfono, y los datos faciales se guardan en el almacenamiento privado de la app, excluidos de las copias de seguridad del teléfono.",
              "En la nube, solo con tu consentimiento a la comparación de caras en la nube: los recortes de caras van a tu propia colección de caras en Amazon Rekognition (Amazon Web Services, región UE (Irlanda)), que solo contiene tus caras. Los recortes se eliminan en cuanto se comparan (lo que quede se elimina pasado un día).",
              "Sin ese consentimiento, todo se queda en tu teléfono. Nunca recibimos tus fotos ni tus vídeos, ni los nombres que das a las personas.",
            ],
          },
        ],
      },
      {
        heading: "3. Para qué se usan y para qué nunca se usan",
        blocks: [
          {
            type: "p",
            text: "Los datos faciales se usan solo para agrupar tus fotos y vídeos por las personas que aparecen en ellos, para que puedas buscar por persona y para ayudarte a elegir qué fotos compartir.",
          },
          {
            type: "p",
            text: "Nunca se venden, nunca se usan para publicidad, perfiles ni verificación de identidad, nunca se usan para identificar a nadie fuera de tus propias fotos, nunca se comparan entre usuarios distintos, nunca se usan para entrenar modelos y nunca se mezclan ni se comparan con los datos faciales de Photohawk Studio. Nunca forman parte de lo que compartes.",
          },
        ],
      },
      {
        heading: "4. Cuánto tiempo se guardan y cuándo se eliminan",
        blocks: [
          {
            type: "p",
            text: "En tu teléfono, los datos faciales se guardan hasta que desactivas la agrupación de caras (o tocas “Delete all face data”), eliminas la app o pasas 3 años sin usar Photohawk. Los datos faciales de una foto desaparecen cuando la foto sale de tu teléfono.",
          },
          {
            type: "p",
            text: "En la nube, los datos faciales se eliminan cuando desactivas la comparación de caras en la nube o la agrupación de caras, cuando eliminas tu cuenta de Photohawk, cuando nos lo pides (en un plazo de 30 días), tras 3 años sin usar Photohawk y 30 días después de que termine tu plan, salvo que vuelvas a suscribirte antes.",
          },
          {
            type: "p",
            text: "La eliminación en la nube borra toda tu colección de caras. Eliminar los datos faciales nunca elimina fotos ni vídeos.",
          },
        ],
      },
      {
        heading: "5. Base jurídica y consentimiento",
        blocks: [
          {
            type: "p",
            text: "Nos basamos en tu consentimiento explícito (art. 9.2.a del RGPD y su equivalente en otras leyes), que se pide dos veces y por separado: primero para la agrupación de caras en tu teléfono (la casilla empieza sin marcar; “Not now” significa que no se detecta ninguna cara), y después para la comparación de caras en la nube (“Continue” o “Keep it on my phone only”). Puedes cambiar cualquiera de los dos en cualquier momento en los ajustes de la app. Si el texto cambia, la app vuelve a preguntar.",
          },
        ],
      },
      {
        heading: "6. Illinois",
        blocks: [
          {
            type: "p",
            text: "La agrupación de caras, y por tanto la comparación de caras en la nube, no se ofrece a quienes indican a la app que viven en Illinois (la app lo pregunta; no usa el GPS ni tu dirección IP). Nuestro servicio también rechaza la comparación en la nube para Illinois, y si cambias tu lugar de residencia a Illinois, los datos faciales existentes se eliminan en el teléfono y en la nube.",
          },
        ],
      },
      {
        heading: "7. Quién los trata",
        blocks: [
          {
            type: "p",
            text: "Solo con la comparación de caras en la nube: Amazon Web Services (Amazon Rekognition), región UE (Irlanda), como nuestro encargado del tratamiento, solo siguiendo nuestras instrucciones. Los datos faciales de tu teléfono nunca se envían a nadie.",
          },
        ],
      },
      {
        heading: "8. Solicitudes y contacto",
        blocks: [
          {
            type: "ul",
            items: [
              "En la app: desactiva la comparación de caras en la nube o la agrupación de caras, o toca “Delete all face data”.",
              "Por correo: support@photohawk.com, para acceder a tus datos faciales en la nube o eliminarlos.",
              CONTACT,
            ],
          },
        ],
      },
    ],
    full: {
      text: "La política de datos faciales completa de la app Photohawk, con su calendario de conservación (en inglés):",
      label: "Photohawk app: Face Data Policy",
      href: "/face-data-policy/family",
    },
  },
  changelog: {
    title: "Cambios",
    items: [
      "1.1, 22 de octubre de 2026: Photohawk Studio, sección 5 reescrita: cuál es la base jurídica para las fotos y para la búsqueda facial, y qué decide el administrador.",
      "1.0, 20 de octubre de 2026: primera versión. Una página con una política propia para Photohawk Studio y otra para Photohawk Family; la política de datos faciales de Photohawk Family deja de ser un borrador.",
    ],
  },
  seeAlso: {
    text: "Cómo se tratan los datos faciales en Photohawk Studio y en la app Photohawk:",
    label: "Política de datos faciales",
  },
  familyPage: {
    seoTitle: "Photohawk app: Face Data Policy | Photohawk",
    description:
      "La política de datos faciales completa de la app Photohawk: qué datos faciales crea, dónde se guardan y su calendario de conservación y destrucción.",
    back: "Política de datos faciales de los dos productos",
    englishOnly: "Este documento solo está disponible en inglés.",
  },
};

export default es;
